/**
 * TransferShipForm Component
 *
 * Shipping mode form for entering shipped quantities with packaging.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see Sprint 22 - Transferts par Packaging
 */

import { Input, Select } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { getQuantityStep } from '@/lib/format';
import { useBackdatingLimit } from '@/hooks/useBackdatingLimit';
import type { TransferDetail, InventoryPackagingOption } from '@bstock/shared';

const BASE_UNIT_VALUE = '__base_unit__';
import type { ShippedLineState } from './useTransferDrawerState';

interface TransferShipFormProps {
  transfer: TransferDetail;
  shippedQuantities: Record<string, ShippedLineState>;
  packagingOptionsMap: Record<string, InventoryPackagingOption[]>;
  onPackagingChange: (lineId: string, value: string) => void;
  onPackagingQtyChange: (lineId: string, qtyStr: string) => void;
  onBaseQtyChange: (lineId: string, qty: number) => void;
  shipDate: Date | null;
  onShipDateChange: (date: Date | null) => void;
}

export function TransferShipForm({
  transfer,
  shippedQuantities,
  packagingOptionsMap,
  onPackagingChange,
  onPackagingQtyChange,
  onBaseQtyChange,
  shipDate,
  onShipDateChange,
}: TransferShipFormProps) {
  const { minDate } = useBackdatingLimit();
  const dateRange: DateRange = { start: shipDate, end: shipDate };

  return (
    <div className="space-y-4">
      {/* Date effective d'expedition (antidatage) */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-dark-500 mb-1.5">
          Date d'expedition
        </label>
        <DatePicker
          asSingle
          showTime
          minDate={minDate}
          maxDate={new Date()}
          value={dateRange}
          onChange={(range: DateRange) => onShipDateChange(range.start)}
          placeholder="Par defaut : aujourd'hui"
        />
        <p className="text-xs text-neutral-400 mt-1">
          Laisser vide pour utiliser la date du jour
        </p>
      </div>
      {transfer.lines.map((line) => {
        const options = packagingOptionsMap[line.rawProductId] ?? [];
        const hasOptions = options.length > 0;
        const shipData = shippedQuantities[line.id];
        const selectedPkg = shipData?.packagingId
          ? options.find((o) => o.packagingId === shipData.packagingId)
          : null;

        return (
          <div key={line.id} className="bg-neutral-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-medium">{line.rawProduct.name}</div>
                <div className="text-xs text-neutral-500">{line.rawProduct.sku}</div>
              </div>
              <div className="text-right text-sm">
                <div className="text-neutral-500">Demande</div>
                <div className="font-medium">
                  {line.quantityRequested} {line.rawProduct.recipeUnit.symbol}
                  {line.requestPackagingName && line.requestPackagingQty != null && (
                    <div className="text-xs text-neutral-400">
                      {line.requestPackagingQty} x {line.requestPackagingName}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Conditionnement + Quantite row */}
            <div className="flex items-end gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-neutral-500 mb-1">Conditionnement</label>
                {hasOptions ? (
                  <Select
                    value={shipData?.packagingId ?? BASE_UNIT_VALUE}
                    onChange={(val) => onPackagingChange(line.id, val)}
                    options={[
                      { value: BASE_UNIT_VALUE, label: `Unite de base (${line.rawProduct.recipeUnit.symbol})` },
                      ...options.map((o) => ({ value: o.packagingId, label: o.label })),
                    ]}
                  />
                ) : (
                  <div className="h-9 flex items-center text-sm text-neutral-600">
                    {line.rawProduct.recipeUnit.symbol}
                  </div>
                )}
              </div>
              <div className="w-28">
                <label className="block text-xs text-neutral-500 mb-1">Quantite</label>
                {shipData?.packagingId ? (
                  <Input
                    type="number"
                    step={getQuantityStep()}
                    min="0"
                    value={shipData.packagingQty}
                    onChange={(e) => onPackagingQtyChange(line.id, e.target.value)}
                  />
                ) : (
                  <Input
                    type="number"
                    step={getQuantityStep()}
                    min="0"
                    value={shipData?.qty ?? line.quantityRequested}
                    onChange={(e) =>
                      onBaseQtyChange(line.id, parseFloat(e.target.value) || 0)
                    }
                  />
                )}
              </div>
            </div>
            {/* Conversion display */}
            {shipData?.packagingId && selectedPkg && (
              <div className="text-xs text-neutral-500">
                = {(parseFloat(shipData.packagingQty) || 0) * (shipData.conversionFactor ?? 0)}{' '}
                {line.rawProduct.recipeUnit.symbol}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
